import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import CustomIconButton from '../../../components/CustomIconButton';
import CustomButton from '../../../components/CustomButton';
import { setTalentSearchForm, searchTalent, setHaveOtherConditionSearch, setCurrentPage, setGlobalSearchTalentFormData, setIsClickSearch, setSelectedRows, setDisplaySelectedCount } from "reducers/companyTalent";
import { getIdFromObject, getListIdFromListObject, checkHasAdvanceFilledKeys } from "../../../utils/util";
import { Modal, Dialog, DialogContent } from '@mui/material';
import { dateToString, trimStringFields } from "utils/format";
import { useNavigate } from 'react-router-dom';
import BasicInfoSection from './TalentSearchForm/BasicInfoSection';
import CareerSection from './TalentSearchForm/CareerSection';
import ReferralsSection from './TalentSearchForm/ReferralsSection';
import AlumniSection from './TalentSearchForm/AlumniSection';
import ActivitySection from './TalentSearchForm/ActivitySection';
import ManagementSection from './TalentSearchForm/ManagementSection';

interface ModalProps {
  open: boolean;
  onClose: () => void;
}

const searchMenu = [
  { name: '基本情報', href: 'basic_info' },
  { name: 'キャリア', href: 'career' },
  { name: 'リファラル', href: 'referrals' },
  { name: 'アルムナイ', href: 'alumni' },
  { name: 'アクティビティ', href: 'activities' },
  { name: '管理項目', href: 'management_items' },
]

const TalentSearchFormDialog : React.FC<ModalProps> = ({open, onClose}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigate = useNavigate();
  const { talentSearchForm, initSearchTalentForm, pageSize, sortBy, globalSearchTalentFormData, selectedRows } = useSelector((state: any) => state.companyTalent);
  const { optionsRelatedCompany, reasonListItems } = useSelector((state: any) => state.talentRegister);
  const [formAdvanceData, setFormAdvanceData] = useState(initSearchTalentForm);
  const [activeMenuItem, setActiveMenuItem] = useState("basic_info");
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isProgrammaticScroll = useRef(false);
  const scrollSession = useRef(0);
  const [dialogHeight, setDialogHeight] = useState(672);

  useEffect(() => {
      setFormAdvanceData(talentSearchForm)
  }, [talentSearchForm]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormAdvanceData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const checkHaveOtherConditionSearch = () => {
    if (checkHasAdvanceFilledKeys(formAdvanceData)) {
      dispatch(setHaveOtherConditionSearch(true));
    } else {
      dispatch(setHaveOtherConditionSearch(false));
    }
  }

  const handleSearchTalent = async(e:any) => {
    if (e) e.preventDefault();
    dispatch(setIsClickSearch(true));
    dispatch(setTalentSearchForm(formAdvanceData));
    const pageNumber = 1;
    dispatch(setCurrentPage(pageNumber));
    
    let data = {
      ...formAdvanceData,
      page: pageNumber,
      page_size: pageSize,
      sort_by: sortBy,
      created_at_start: formAdvanceData.created_at_start ? dateToString(formAdvanceData.created_at_start) : null,
      created_at_end: formAdvanceData.created_at_end ? dateToString(formAdvanceData.created_at_end) : null,
      date_of_birth_start: formAdvanceData.date_of_birth_start ? dateToString(formAdvanceData.date_of_birth_start) : null,
      date_of_birth_end: formAdvanceData.date_of_birth_end ? dateToString(formAdvanceData.date_of_birth_end) : null,
      gender: getIdFromObject(formAdvanceData.gender),
      profession_1_id: getIdFromObject(formAdvanceData.profession_1_id),
      profession_2_id: getIdFromObject(formAdvanceData.profession_2_id),
      profession_3_id: getIdFromObject(formAdvanceData.profession_3_id),
      industry_1_id: getIdFromObject(formAdvanceData.industry_1_id),
      industry_2_id: getIdFromObject(formAdvanceData.industry_2_id),
      industry_3_id: getIdFromObject(formAdvanceData.industry_3_id),
      type: formAdvanceData?.type?.map(item => getIdFromObject(item)),
      work_parttime: getListIdFromListObject(formAdvanceData.work_parttime),
      change_job: getListIdFromListObject(formAdvanceData.change_job),
      status: getListIdFromListObject(formAdvanceData.status),
      activity_level: getIdFromObject(formAdvanceData.activity_level),
      job_view_level: getIdFromObject(formAdvanceData.job_view_level),
      access_time: getIdFromObject(formAdvanceData.access_time),
      recruitment_rank: getIdFromObject(formAdvanceData.recruitment_rank),
      city: getListIdFromListObject(formAdvanceData.city),
      scout_history: getIdFromObject(formAdvanceData.scout_history),
      status_date_rank: getIdFromObject(formAdvanceData.status_date_rank),
      tags: getListIdFromListObject(formAdvanceData.tags),
      tag_condition: getIdFromObject(formAdvanceData.tag_condition),
      expected_condition: getListIdFromListObject(formAdvanceData.expected_condition, '|'),
      preferred_city_id: getListIdFromListObject(formAdvanceData.preferred_city_id),
      working_style_id: getListIdFromListObject(formAdvanceData.working_style_id),
      is_referred: formAdvanceData.is_referred ? true : null,
      reason_resignation: getListIdFromListObject(formAdvanceData.reason_resignation, '|'),
      last_job_viewed_time: getIdFromObject(formAdvanceData.last_job_viewed_time)
    };

    dispatch(setCurrentPage(1));
    data = trimStringFields(data);
    const retrievedTalents = await dispatch(searchTalent({ requestBody: data }));
    if(retrievedTalents?.payload?.data?.talents?.length > 0) {
      // Only filter selected rows when searching from input form or clicking search button, not when sorting or paginating, to avoid confusion for users
      const talents = retrievedTalents?.payload?.data?.talents;
      filterSelectedRowsWhenSearching(talents);
    }
    checkHaveOtherConditionSearch();

    saveConditionSearch(1, sortBy)
    handleClose();
  };

  const filterSelectedRowsWhenSearching = (talents) => {
    const talentIds = new Set(talents.filter((n: any) => n.is_active == true).map((n: any) => n.id));
    dispatch(setSelectedRows(selectedRows.filter(id => talentIds.has(id))));
    dispatch(setDisplaySelectedCount(selectedRows.filter(id => talentIds.has(id)).length));
  }

  const saveConditionSearch = (page, sortBy) => {
    const globalFormData = {
      ...globalSearchTalentFormData,
      page: page,
      page_size: pageSize,
      sort_by: sortBy,
      ...formAdvanceData,
    }
    dispatch(setGlobalSearchTalentFormData(globalFormData));
  };

  const clearSearchForm = () => {
      dispatch(setTalentSearchForm(initSearchTalentForm));
      dispatch(setGlobalSearchTalentFormData(null));
      setFormAdvanceData(initSearchTalentForm);
  };

  const handleClose = () => {
    onClose()
  }

  useEffect(() => {
    if (!open) return;

    const timeout = setTimeout(() => {
      if (!dialogRef.current) return;

      const container = dialogRef.current?.querySelector(
        ".overflow-y-auto"
      );

      if (!container) return;

      const sections = container.querySelectorAll("section");

      const observer = new IntersectionObserver(
        (entries) => {
          if (isProgrammaticScroll.current) return;

          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveMenuItem(entry.target.id);
            }
          });
        },
        {
          root: container,
          rootMargin: "-50% 0px -40% 0px",
          threshold: 0,
        }
      );

      sections.forEach((section) => {
        if(!isProgrammaticScroll.current) observer.observe(section)
       }
      );

      return () => observer.disconnect();
    }, 100); // wait for mount

    return () => clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    const updateHeight = () => {
      if (window.innerHeight < 1000) {
        setDialogHeight(672);
      } else {
        setDialogHeight(800);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const handleMenuClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string
  ) => {
    e.preventDefault();
 
    const container = scrollContainerRef.current;
    const section = container?.querySelector(`#${id}`);
 
    if (!container || !section) return;
 
    isProgrammaticScroll.current = true;
    setActiveMenuItem(id);
 
    const handleScrollEnd = () => {
      isProgrammaticScroll.current = false;
      container.removeEventListener("scrollend", handleScrollEnd);
    };

    if(isProgrammaticScroll.current == true) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    container.addEventListener("scrollend", handleScrollEnd);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      scroll="paper"
      PaperProps={{
        sx: {
          width: "65rem",
          borderRadius: "16px",
          height: `${dialogHeight}px`,
        },
      }}
    >
      <div className='flex items-center justify-between px-6 py-4 border-b border-b-[#E0E0E0]'>
        <h2 className='text-xl font-bold alu-text-base-2'>詳細検索</h2>
        <CustomIconButton onClick={handleClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <g clipPath="url(#clip0_8574_265)">
              <path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill="currentColor"
              />
            </g>
            <defs>
              <clipPath id="clip0_8574_265">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </CustomIconButton>
      </div>

      <DialogContent
        dividers
        ref={dialogRef}
        sx={{
          display: "flex",
          padding: 0,
        }}
      >
        <div className='pl-6 pr-10 min-h-full py-6'>
          <ul className='flex flex-col gap-4'>
            {searchMenu.map((item) => (
              <li key={item.href} className='flex items-center gap-2'>
                {activeMenuItem === item.href ? (
                  <span className=''>
                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                      <circle cx="4" cy="4" r="4" fill="#FF861A"/>
                    </svg>
                  </span>
                ) : (
                  <span className='w-2'></span>
                )
                }
                <a href={`#${item.href}`} onClick={(e) => handleMenuClick(e, item.href)} className={`text-sm font-bold leading-[21px] ${activeMenuItem === item.href ? "alu-text-base-2 pointer-events-none" : "alu-text-base-1 hover:text-[#FF861A]"}`}>{item.name}</a>
              </li>
            ))}
          </ul>
        </div>

        <div ref={scrollContainerRef} className='flex-1 pt-6 pb-[3.5rem] pl-6 pr-8 overflow-y-auto custom-scrollbar scroll-smooth'>
          {/* 基本情報 */}
          <section id="basic_info" className='pb-10 border-b border-b-[#E0E0E0] scroll-mt-6'>
            <BasicInfoSection 
              handleSearchTalent={handleSearchTalent}
              formAdvanceData={formAdvanceData}
              setFormAdvanceData={setFormAdvanceData}
              handleChange={handleChange}
            />
          </section>

          {/* キャリア */}
          <section id="career" className='py-10 border-b border-b-[#E0E0E0]'>
            <CareerSection 
              formAdvanceData={formAdvanceData}
              setFormAdvanceData={setFormAdvanceData}
            />
          </section>

          {/* リファラル */}
          <section id="referrals" className='py-10 border-b border-b-[#E0E0E0]'>
            <ReferralsSection
              formAdvanceData={formAdvanceData}
              setFormAdvanceData={setFormAdvanceData}
              handleChange={handleChange}
              handleSearchTalent={handleSearchTalent}
            />
          </section>

          {/* アルムナイ */}
          <section id="alumni" className='py-10 border-b border-b-[#E0E0E0]'>
            <AlumniSection
              formAdvanceData={formAdvanceData}
              setFormAdvanceData={setFormAdvanceData}
              handleChange={handleChange}
              handleSearchTalent={handleSearchTalent}
            />
          </section>

          {/* アクティビティ */}
          <section id="activities" className='py-10 border-b border-b-[#E0E0E0]'>
            <ActivitySection
              formAdvanceData={formAdvanceData}
              setFormAdvanceData={setFormAdvanceData}
            />
          </section>

          {/* 管理項目 */}
          <section id="management_items" className='pt-10'>
            <ManagementSection
              formAdvanceData={formAdvanceData}
              setFormAdvanceData={setFormAdvanceData}
              handleChange={handleChange}
              handleSearchTalent={handleSearchTalent}
            />
          </section>
        </div>
      </DialogContent>

      <div className='px-6 py-4 bg-white flex justify-center items-center gap-8 border-t border-t-[#E0E0E0] rounded-b-2xl'>
        <CustomButton type="button" variant="text" onClick={clearSearchForm}>クリア</CustomButton>
        <CustomButton type="button" size="md" customStyle='!w-[10rem]' onClick={handleSearchTalent}>検索</CustomButton>
      </div>
    </Dialog>
  )
}

export default TalentSearchFormDialog